/**
 * What the activity modal reads off an activity, and what it sends back.
 *
 * Both directions are explicit picks over the lists below, replacing a `{...activity}` spread.
 * The spread was not a style problem: the modal fetched a full activity and posted the whole
 * object back, so the moment the API grew the profile columns every save started carrying them,
 * and the server's "you may not send a seed-owned field" rule rejected every edit in the admin
 * UI. Anything not named here cannot reach the wire, whatever the server starts returning.
 */

import type { Activity } from '../../../types/Activity';
import type { ActivityWritePayload } from '../../../services/activityService';

/** The plain activity fields the Details tab edits. */
export const DETAIL_FIELDS = [
  'activityName',
  'activityNumber',
  'unitOfMeasure',
  'classification',
  'locationCode',
  'activityType',
  'point',
  'linear',
  'activityProfile',
  'threshold',
] as const;

/** The 19 profile attributes the Profile tab edits. */
export const PROFILE_FIELDS = [
  'displayQuantityToUser',
  'requiresLocationalData',
  'askForRoad',
  'mechanicEquipmentActivity',
  'signActivity',
  'culvertActivity',
  'cattleGuardActivity',
  'wildlifeActivity',
  'rockfallActivity',
  'restAreaActivity',
  'structureActivity',
  'requiresStructure',
  'briefAndEmergingActivity',
  'blockActivity',
  'blockType',
  'displayEntireRoadQuestion',
  'numberOfPasses',
  'numberedRoadActivity',
  'isLunch',
] as const;

/** The 13 that are plain yes/no. The other six each have a state that is not a boolean. */
export const PROFILE_BOOLEAN_FIELDS = [
  'displayQuantityToUser',
  'requiresLocationalData',
  'askForRoad',
  'mechanicEquipmentActivity',
  'signActivity',
  'culvertActivity',
  'cattleGuardActivity',
  'wildlifeActivity',
  'rockfallActivity',
  'requiresStructure',
  'briefAndEmergingActivity',
  'blockActivity',
  'isLunch',
] as const;

export type ProfileBooleanField = (typeof PROFILE_BOOLEAN_FIELDS)[number];

/** Readable names for the flags, which are otherwise column names on a screen. */
export const PROFILE_FIELD_LABELS: Record<string, string> = {
  displayQuantityToUser: 'Ask for a quantity',
  requiresLocationalData: 'Requires locational data',
  askForRoad: 'Ask for a road',
  mechanicEquipmentActivity: 'Mechanic / equipment activity',
  signActivity: 'Sign activity',
  culvertActivity: 'Culvert activity',
  cattleGuardActivity: 'Cattle guard activity',
  wildlifeActivity: 'Wildlife activity',
  rockfallActivity: 'Rockfall activity',
  requiresStructure: 'Requires a structure',
  briefAndEmergingActivity: 'Brief and emerging activity',
  blockActivity: 'Block activity',
  isLunch: 'Is lunch',
};

/**
 * The values `Block Type` takes in the export.
 *
 * Offered as a datalist rather than a select: the value is stored verbatim and passed to the
 * blocks datasource as a filter, so a select would silently blank anything not on this list
 * the day someone adds a seventh block type to the export.
 */
export const BLOCK_TYPE_SUGGESTIONS = [
  'Garbage',
  'Sweeping',
  'Mowing',
  'Patrol',
  'Grading',
  'Plow',
];

/** The form's own shape: everything a control can hold, before it is converted back. */
export interface ActivityFormState {
  activityName: string;
  activityNumber: string;
  unitOfMeasure: string;
  classification: string;
  locationCode: string;
  activityType: string;
  point: boolean;
  linear: boolean;
  activityProfile: string;
  threshold: number | null;

  // Profile attributes. The tri-states are held as the strings their selects use, and
  // converted back on save, so '' can mean null without being confused with false.
  displayQuantityToUser: boolean;
  requiresLocationalData: boolean;
  askForRoad: boolean;
  mechanicEquipmentActivity: boolean;
  signActivity: boolean;
  culvertActivity: boolean;
  cattleGuardActivity: boolean;
  wildlifeActivity: boolean;
  rockfallActivity: boolean;
  requiresStructure: boolean;
  briefAndEmergingActivity: boolean;
  blockActivity: boolean;
  isLunch: boolean;
  restAreaActivity: string;
  structureActivity: string;
  displayEntireRoadQuestion: string;
  numberedRoadActivity: string;
  numberOfPasses: string;
  blockType: string;

  // Work instructions. Steps are edited as one per line.
  workInstructionPurpose: string;
  workInstructionSteps: string;
}

export const emptyActivityForm = (): ActivityFormState => ({
  activityName: '',
  activityNumber: '',
  unitOfMeasure: '',
  classification: '',
  locationCode: '',
  activityType: '',
  point: false,
  linear: false,
  activityProfile: '',
  threshold: null,

  displayQuantityToUser: false,
  requiresLocationalData: false,
  askForRoad: false,
  mechanicEquipmentActivity: false,
  signActivity: false,
  culvertActivity: false,
  cattleGuardActivity: false,
  wildlifeActivity: false,
  rockfallActivity: false,
  requiresStructure: false,
  briefAndEmergingActivity: false,
  blockActivity: false,
  isLunch: false,
  restAreaActivity: '',
  structureActivity: '',
  displayEntireRoadQuestion: '',
  numberedRoadActivity: '',
  numberOfPasses: '',
  blockType: '',

  workInstructionPurpose: '',
  workInstructionSteps: '',
});

/** A tri-state boolean as its select value. Null and false are different instructions. */
const triStateToString = (value: boolean | null | undefined): string => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
};

export const toActivityFormState = (activity?: Activity): ActivityFormState => {
  const empty = emptyActivityForm();
  if (!activity) return empty;

  return {
    ...empty,
    activityName: activity.activityName ?? '',
    activityNumber: activity.activityNumber ?? '',
    unitOfMeasure: activity.unitOfMeasure ?? '',
    classification: activity.classification ?? '',
    locationCode: activity.locationCode ?? '',
    activityType: activity.activityType ?? '',
    point: activity.point ?? false,
    linear: activity.linear ?? false,
    activityProfile: activity.activityProfile ?? '',
    threshold: activity.threshold ?? null,

    displayQuantityToUser: activity.displayQuantityToUser ?? false,
    requiresLocationalData: activity.requiresLocationalData ?? false,
    askForRoad: activity.askForRoad ?? false,
    mechanicEquipmentActivity: activity.mechanicEquipmentActivity ?? false,
    signActivity: activity.signActivity ?? false,
    culvertActivity: activity.culvertActivity ?? false,
    cattleGuardActivity: activity.cattleGuardActivity ?? false,
    wildlifeActivity: activity.wildlifeActivity ?? false,
    rockfallActivity: activity.rockfallActivity ?? false,
    requiresStructure: activity.requiresStructure ?? false,
    briefAndEmergingActivity: activity.briefAndEmergingActivity ?? false,
    blockActivity: activity.blockActivity ?? false,
    isLunch: activity.isLunch ?? false,
    restAreaActivity: activity.restAreaActivity ?? '',
    structureActivity: activity.structureActivity ?? '',
    displayEntireRoadQuestion: activity.displayEntireRoadQuestion ?? '',
    numberedRoadActivity: triStateToString(activity.numberedRoadActivity),
    numberOfPasses: activity.numberOfPasses == null ? '' : String(activity.numberOfPasses),
    blockType: activity.blockType ?? '',

    workInstructionPurpose: activity.workInstructions?.purpose ?? '',
    workInstructionSteps: (activity.workInstructions?.steps ?? []).join('\n'),
  };
};

/**
 * Whether the work instructions were actually fetched.
 *
 * `workInstructions: null` means "not fetched", which is not the same as "there are none" - the
 * prose is omitted from list queries by default. The activity view falls back to the list row
 * if the detail fetch fails, and saving from that state would write emptiness over real
 * procedure, so the instruction fields are left out of the payload entirely in that case.
 */
export const hasFetchedInstructions = (activity?: Activity): boolean =>
  !activity || activity.workInstructions != null || !activity.hasWorkInstructions;

export type { ActivityWritePayload } from '../../../services/activityService';

/** Build the write body. Only the fields listed above can appear in it. */
export const toActivityPayload = (
  form: ActivityFormState,
  options: { includeWorkInstructions: boolean }
): ActivityWritePayload => {
  const payload: ActivityWritePayload = {
    activityName: form.activityName,
    activityNumber: form.activityNumber,
    unitOfMeasure: form.unitOfMeasure,
    classification: form.classification,
    locationCode: form.locationCode,
    activityType: form.activityType,
    point: form.point,
    linear: form.linear,
    activityProfile: form.activityProfile,
    threshold: form.threshold,

    restAreaActivity: form.restAreaActivity || null,
    structureActivity: form.structureActivity || null,
    displayEntireRoadQuestion: form.displayEntireRoadQuestion || null,
    numberedRoadActivity:
      form.numberedRoadActivity === '' ? null : form.numberedRoadActivity === 'true',
    numberOfPasses: form.numberOfPasses === '' ? null : Number(form.numberOfPasses),
    blockType: form.blockType.trim() === '' ? null : form.blockType.trim(),
  };

  for (const field of PROFILE_BOOLEAN_FIELDS) {
    payload[field] = form[field];
  }

  if (options.includeWorkInstructions) {
    payload.workInstructionPurpose = form.workInstructionPurpose.trim() || null;
    payload.workInstructionSteps = form.workInstructionSteps
      .split('\n')
      .map(step => step.trim())
      .filter(Boolean);
  }

  return payload;
};
